import type { ReactElement } from 'react';

import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { translate } from '@web-platform/shared-universal-translations';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import classNames from 'classnames';

import { SponsoredInfoButton } from '../SponsoredInfoButton/SponsoredInfoButton';

import STYLES from './SponsoredText.module.scss';

type SponsoredTextProps = {
  translucent?: boolean;
  className?: string;
  infoIconCustomColor?: boolean;
  withInfoIcon?: boolean;
  ariaLabel?: string;
  'aria-hidden'?: boolean;
  additionalText?: string;
  additionalTitle?: string;
  sponsoredText?: string;
  pioPartnerName?: string;
  pioAgencyName?: string;
};

export const SponsoredText = ({
  additionalText,
  additionalTitle,
  'aria-hidden': ariaHidden = false,
  ariaLabel,
  className,
  infoIconCustomColor = false,
  pioAgencyName,
  pioPartnerName,
  sponsoredText,
  translucent = false,
  withInfoIcon = false,
}: SponsoredTextProps): ReactElement => {
  const customIconColorClass = {
    [STYLES.infoIconCustomColor]: infoIconCustomColor,
  };

  const textClassNames = [STYLES.sponsoredText];

  if (translucent) {
    textClassNames.push(STYLES.translucentText);
  }

  if (infoIconCustomColor) {
    textClassNames.push(STYLES.textCustomColor);
  }

  if (className) {
    textClassNames.push(className);
  }

  const resultingClassName = textClassNames.join(' ');

  const dsaModalEnabled = getFeatureDecision(
    FEATURE_DECISION_KEYS.ADS_DSA_MODAL_UPDATED_WITH_LEGAL_NAME_WEB,
  );

  const prepareCallToActionData = () => {
    let whySeeingDescription: string;
    if (!dsaModalEnabled || !pioPartnerName) {
      whySeeingDescription = translate('ktxt_Ad_info_message');
    } else if (pioAgencyName && pioAgencyName !== pioPartnerName) {
      whySeeingDescription = translate('ktxt_dsa_why_seeing_body_with_agency', {
        partner: pioPartnerName,
        agency: pioAgencyName,
      });
    } else {
      whySeeingDescription = translate('ktxt_dsa_why_seeing_body_single', {
        partner: pioPartnerName,
      });
    }

    const bottomSheetContent = [
      {
        title: '',
        description: whySeeingDescription,
      },
      {
        title: translate('ktxt_dsa_based_on_now_title'),
        description: translate('ktxt_dsa_based_on_now_body'),
      },
      {
        title: translate('ktxt_dsa_based_on_past_title'),
        description: translate('ktxt_dsa_based_on_past_body'),
      },
    ];

    if (additionalText && additionalTitle) {
      bottomSheetContent.push({
        title: additionalTitle,
        description: additionalText,
      });
    }

    return {
      text: translate('ktxt_promotion_sponsored'),
      bottomSheetContent,
      bottomSheetTitle: translate('ktxt_ad_info_title'),
    };
  };
  const callToActionData = prepareCallToActionData();
  return withInfoIcon ? (
    <div className={classNames(customIconColorClass)}>
      <SponsoredInfoButton
        infoIconCustomColor={infoIconCustomColor}
        aria-hidden={ariaHidden}
        ariaLabel={ariaLabel}
        callToAction={callToActionData}
      >
        <BpkText className={classNames(resultingClassName)} textStyle={TEXT_STYLES.xs}>
          {sponsoredText || translate('ktxt_promotion_sponsored')}
        </BpkText>
      </SponsoredInfoButton>
    </div>
  ) : (
    <BpkText className={resultingClassName} textStyle={TEXT_STYLES.xs}>
      {sponsoredText || translate('ktxt_promotion_sponsored')}
    </BpkText>
  );
};
